/**
 * FitnessSettingsSection - Fitness Level and Equipment settings
 * Created by neo on 21.01.2026
 */
import * as React from 'react';
import { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useSaveAthlete } from '../../Hooks/useAthleteMutations';
import { getTagValue, setTagCategory } from '../../Model/Athlete/Athlete';
import { Theme } from '../../Theme/Theme';
import { Button } from '../../Components/Button';
import { useAthleteEquipment } from '../../Hooks/useAthleteQueries';

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardHeader = styled.div`
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OptionCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 12px;
  background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : Theme.Colors.lightestGrey)};
  }
`;

const OptionIcon = styled.div<{ $selected?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  background: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${(props) => (props.$selected ? 'white' : Theme.Colors.warmGrey)};
  }
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div<{ $selected?: boolean }>`
  font-size: 16px;
  font-weight: ${(props) => (props.$selected ? '600' : '400')};
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
  margin-bottom: 4px;
`;

const OptionDescription = styled.div`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const RadioIndicator = styled.div<{ $selected?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 6px;
    background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
  }
`;

const SaveButton = styled(Button)`
  margin-top: 16px;
`;

const SuccessMessage = styled.div`
  color: ${Theme.Colors.primary};
  font-size: 14px;
  margin-top: 8px;
`;

const WalkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14.12 10H19V8.2h-3.62l-2.36-3.93c-.46-.77-1.31-1.27-2.24-1.27-.58 0-1.14.19-1.59.53L5.06 7.24c-.68.55-.94 1.46-.6 2.24l1.03 2.52-3.49 3 1.4 1.65 4.3-3.69.88 2.17-1.98 1.99c-.35.37-.56.85-.56 1.39 0 .5.19.98.53 1.35l3.43 3.63 1.64-1.54-2.91-3.08.87-.88-.26-.67C9.75 15.21 11 13.24 11 11c0-.4-.05-.79-.13-1.17L9.84 8l1.76-1.45.93 1.55c.39.65 1.1 1.07 1.88 1.07H14.12z" />
    <circle cx="14" cy="3.5" r="1.75" />
  </svg>
);

const RunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 5.28c-1.23-.37-2.22-1.17-2.8-2.18l-1-1.6c-.41-.65-1.11-1-1.84-1-.78 0-1.59.5-1.78 1.44S7 15 7 15h2l1.5-4.5 2.5 2v6h2v-7.5l-2.5-1.85.74-2.14c.8.93 1.88 1.66 3.11 2.05l.15-2.78z" />
  </svg>
);

const RunFastIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L8 9.8V14h2v-3l1.5-.7-.9 4.1-3.6-.9V18H5v-4.5l3-1.6v2.4l4.5 1.1zM1 14v-2h6v2H1zm0 4h6v-2H1v2zm0-8V8h6v2H1z" />
  </svg>
);

type FitnessLevel = 'beginner' | 'intermediate' | 'advanced';

export const FitnessSettingsSection: React.FC = observer(() => {
  const { t } = useTrans();
  const { authentication } = useRootStore();
  const athlete = authentication.athlete;

  const [selectedLevel, setSelectedLevel] = useState<FitnessLevel>(
    (athlete ? getTagValue(athlete, 'level') as FitnessLevel : undefined) ?? 'beginner',
  );
  const [savingLevel, setSavingLevel] = useState(false);
  const [levelSaved, setLevelSaved] = useState(false);

  const { data: athleteEquipment } = useAthleteEquipment();

  const fitnessLevelOptions = [
    {
      label: t('profileSettingsFitnessLevelModal.beginnerItem', { defaultValue: 'Beginner' }),
      description: t('editFitnessLevelScreen.beginnerDescription', {
        defaultValue: 'Just starting out or getting back into fitness',
      }),
      value: 'beginner' as FitnessLevel,
      icon: <WalkIcon />,
    },
    {
      label: t('profileSettingsFitnessLevelModal.intermediateItem', { defaultValue: 'Intermediate' }),
      description: t('editFitnessLevelScreen.intermediateDescription', {
        defaultValue: 'Regular workouts with moderate intensity',
      }),
      value: 'intermediate' as FitnessLevel,
      icon: <RunIcon />,
    },
    {
      label: t('profileSettingsFitnessLevelModal.advancedItem', { defaultValue: 'Advanced' }),
      description: t('editFitnessLevelScreen.advancedDescription', {
        defaultValue: 'Experienced with high-intensity training',
      }),
      value: 'advanced' as FitnessLevel,
      icon: <RunFastIcon />,
    },
  ];

  const saveAthlete = useSaveAthlete();

  const handleSaveFitnessLevel = useCallback(async () => {
    if (!athlete) return;
    setSavingLevel(true);
    setLevelSaved(false);
    try {
      const updatedAthlete = { ...athlete, tags: setTagCategory(athlete.tags, 'level', selectedLevel) };
      await saveAthlete.mutateAsync(updatedAthlete);
      // Update the store's athlete tags in place
      runInAction(() => {
        if (authentication.athlete) {
          authentication.athlete.tags = updatedAthlete.tags;
        }
      });
      setLevelSaved(true);
      setTimeout(() => setLevelSaved(false), 3000);
    } finally {
      setSavingLevel(false);
    }
  }, [athlete, selectedLevel, saveAthlete, authentication]);

  return (
    <SectionContent>
      <SettingsCard>
        <CardHeader>
          <CardTitle>{t('settings.fitnessLevel', { defaultValue: 'Fitness Level' })}</CardTitle>
          <CardDescription>
            {t('editFitnessLevelScreen.description', {
              defaultValue: 'Choose your current fitness level to get personalized workout recommendations.',
            })}
          </CardDescription>
        </CardHeader>

        <OptionsGrid>
          {fitnessLevelOptions.map((option) => {
            const selected = selectedLevel === option.value;
            return (
              <OptionCard key={option.value} $selected={selected} onClick={() => setSelectedLevel(option.value)}>
                <OptionIcon $selected={selected}>{option.icon}</OptionIcon>
                <OptionContent>
                  <OptionLabel $selected={selected}>{option.label}</OptionLabel>
                  <OptionDescription>{option.description}</OptionDescription>
                </OptionContent>
                <RadioIndicator $selected={selected} />
              </OptionCard>
            );
          })}
        </OptionsGrid>

        <SaveButton color="primary" size="sm" onClick={handleSaveFitnessLevel} disabled={savingLevel}>
          {savingLevel
            ? t('common.saving', { defaultValue: 'Saving...' })
            : t('editProfileScreen.saveChanges', { defaultValue: 'Save Changes' })}
        </SaveButton>
        {levelSaved && (
          <SuccessMessage>
            {t('editFitnessLevelScreen.levelUpdateSuccess', { defaultValue: 'Fitness level updated successfully!' })}
          </SuccessMessage>
        )}
      </SettingsCard>
    </SectionContent>
  );
});
